import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, ExternalLink, HardDriveDownload, HardDriveUpload, Loader2, UploadCloud, X } from 'lucide-react';
import InfoTip from './InfoTip';
import { getNasMatchKeys, nasAuthorsMatch, normalizeBookTitle } from '../copyrightRegistry';
import { formatBytes, useNas } from './NasProvider';

const FOLDER_CHARS = /[\\/:*?"<>|]/g;
const cleanFolder = value => String(value || '').replace(FOLDER_CHARS, ' ').replace(/\s+/g, ' ').trim();

function NasUploadModal({ task, onClose }) {
    const { uploads, callApi, suggestions } = useNas();
    const waiting = uploads[task.id]?.status === 'waiting' ? uploads[task.id] : null;
    const [step, setStep] = useState(waiting ? 'upload' : 'target');
    const [form, setForm] = useState({
        genre: waiting?.genre || '',
        author: waiting?.author || task.author || '',
        work: waiting?.work || task.bookTitle || ''
    });
    const [upload, setUpload] = useState(waiting);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        const onKey = event => { if (event.key === 'Escape' && !busy) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const existingWork = useMemo(() => suggestions.works.find(item =>
        normalizeBookTitle(item.work) === normalizeBookTitle(form.work)
        && normalizeBookTitle(item.author) === normalizeBookTitle(form.author)), [suggestions.works, form.work, form.author]);

    // Same author, near-identical title (e.g. "Blade's Rest Series Book 1"): offer the old folder.
    const similarWork = useMemo(() => {
        if (existingWork || !form.author.trim()) return null;
        const keys = new Set(getNasMatchKeys(form.work));
        if (!keys.size) return null;
        return suggestions.works.find(item => nasAuthorsMatch(item.author, form.author)
            && getNasMatchKeys(item.work).some(key => keys.has(key))) || null;
    }, [existingWork, suggestions.works, form.work, form.author]);

    const pickWork = value => {
        const match = suggestions.works.find(item => item.work === value);
        setForm(current => match ? { genre: match.genre, author: match.author, work: match.work } : { ...current, work: value });
    };

    const run = async (kind, fn) => {
        setBusy(kind);
        setError('');
        try { await fn(); } catch (err) { setError(err.message); } finally { setBusy(''); }
    };

    const start = () => run('start', async () => {
        const target = { genre: cleanFolder(form.genre), author: cleanFolder(form.author), work: cleanFolder(form.work) };
        if (!target.genre || !target.author || !target.work) throw new Error('Hãy nhập đủ 3 ô.');
        const data = await callApi('start-upload', { taskId: task.id, ...target });
        setUpload(data.upload);
        setStep('upload');
    });

    const finish = () => run('finish', async () => {
        const data = await callApi('finish-upload', { taskId: task.id });
        setResult(data);
        setStep('done');
    });

    const cancel = () => {
        if (!window.confirm('Hủy lượt upload này? Các file đã upload vào link sẽ bị xóa.')) return;
        run('cancel', async () => {
            await callApi('cancel-upload', { taskId: task.id });
            onClose();
        });
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(upload.uploadUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (_) {
            window.prompt('Sao chép link upload:', upload.uploadUrl);
        }
    };

    return createPortal(
        <div className="nas-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
            <div className="nas-modal" role="dialog" aria-modal="true" aria-label="Lưu vào NAS">
                <header className="nas-modal-head">
                    <div>
                        <span className="nas-kicker">Lưu vào NAS</span>
                        <h3 title={task.bookTitle}>{task.bookTitle}</h3>
                    </div>
                    <button type="button" className="nas-icon-btn" onClick={onClose} disabled={Boolean(busy)} aria-label="Đóng"><X size={18} /></button>
                </header>

                <ol className="nas-steps" aria-label="Các bước">
                    {['Chọn thư mục', 'Upload file', 'Hoàn tất'].map((label, index) => {
                        const current = ['target', 'upload', 'done'].indexOf(step);
                        return <li key={label} className={index < current ? 'is-done' : index === current ? 'is-active' : ''}><span>{index < current ? <Check size={12} /> : index + 1}</span>{label}</li>;
                    })}
                </ol>

                {step === 'target' && (
                    <div className="nas-modal-body">
                        <div className="nas-field-title">
                            Thư mục lưu trên NAS
                            <InfoTip>
                                Kho NAS xếp theo <b>Thể loại / Tác giả / Tác phẩm</b>. Series nhiều cuốn thì để chung một Tác phẩm (ví dụ “The Resonance Cycle series”).
                                Ưu tiên chọn gợi ý có sẵn để không tạo thư mục trùng. Nhiều tác giả thì cách nhau bằng dấu phẩy.
                            </InfoTip>
                        </div>
                        <label className="nas-field">
                            <span>Thể loại</span>
                            <input list="nas-genres" value={form.genre} onChange={event => setForm({ ...form, genre: event.target.value })} placeholder="VD: LitRPG" maxLength={120} autoFocus />
                        </label>
                        <label className="nas-field">
                            <span>Tác giả</span>
                            <input list="nas-authors" value={form.author} onChange={event => setForm({ ...form, author: event.target.value })} placeholder="VD: Aaron Renfroe" maxLength={120} />
                        </label>
                        <label className="nas-field">
                            <span>Tác phẩm</span>
                            <input list="nas-works" value={form.work} onChange={event => pickWork(event.target.value)} placeholder="Tên sách hoặc tên series" maxLength={120} />
                        </label>
                        <datalist id="nas-genres">{suggestions.genres.map(value => <option key={value} value={value} />)}</datalist>
                        <datalist id="nas-authors">{suggestions.authors.map(value => <option key={value} value={value} />)}</datalist>
                        <datalist id="nas-works">{suggestions.works.map(item => <option key={item.id} value={item.work}>{item.author}</option>)}</datalist>
                        <div className="nas-path-preview">
                            Audiobook › {cleanFolder(form.genre) || '…'} › {cleanFolder(form.author) || '…'} › {cleanFolder(form.work) || '…'}
                        </div>
                        {existingWork && <p className="nas-hint">Thư mục này đã có {existingWork.audio} file. File mới sẽ được thêm vào chung.</p>}
                        {similarWork && (
                            <p className="nas-hint">
                                Đã có thư mục gần giống: “{similarWork.work}” ({similarWork.audio} file).{' '}
                                <button type="button" className="nas-link-btn" onClick={() => setForm({ genre: similarWork.genre, author: similarWork.author, work: similarWork.work })}>Dùng thư mục này</button>
                            </p>
                        )}
                        {error && <p className="nas-error">{error}</p>}
                        <div className="nas-modal-actions">
                            <button type="button" className="nas-btn is-primary" onClick={start} disabled={Boolean(busy)}>
                                {busy === 'start' ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />} Tạo link upload
                            </button>
                        </div>
                    </div>
                )}

                {step === 'upload' && upload && (
                    <div className="nas-modal-body">
                        <p className="nas-lead">
                            Mở trang upload và kéo thả file audio vào.
                            <InfoTip>
                                Chỉ cần file audio (mp3, m4b, m4a…); có thư mục con từ torrent cũng được, hệ thống sẽ gom lại.
                                Bạn có thể đóng cửa sổ này trong lúc upload, sau đó bấm lại nút <b>Đang lưu NAS</b> ở task để hoàn tất.
                                Link upload dùng được đến {upload.expires}.
                            </InfoTip>
                        </p>
                        <a className="nas-btn is-primary is-block" href={upload.uploadUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Mở trang upload</a>
                        <button type="button" className="nas-btn is-ghost is-block" onClick={copyLink}>{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Đã sao chép' : 'Sao chép link'}</button>
                        <div className="nas-path-preview">Audiobook › {upload.genre} › {upload.author} › {upload.work}</div>
                        {error && <p className="nas-error">{error}</p>}
                        <div className="nas-modal-actions is-split">
                            <button type="button" className="nas-btn is-text-danger" onClick={cancel} disabled={Boolean(busy)}>{busy === 'cancel' ? 'Đang hủy…' : 'Hủy'}</button>
                            <button type="button" className="nas-btn is-success" onClick={finish} disabled={Boolean(busy)}>
                                {busy === 'finish' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Tôi đã upload xong
                            </button>
                        </div>
                    </div>
                )}

                {step === 'done' && result && (
                    <div className="nas-modal-body nas-done">
                        <div className="nas-done-icon"><Check size={28} /></div>
                        <strong>Đã lưu vào NAS</strong>
                        <p>{result.audioCount} file audio · {formatBytes(result.totalBytes)}</p>
                        {result.skipped?.length > 0 && <p className="nas-hint">{result.skipped.length} file trùng tên đã có sẵn nên được bỏ qua.</p>}
                        <button type="button" className="nas-btn is-primary" onClick={onClose}>Xong</button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

// Task row action: download from NAS when archived, otherwise offer to save it there.
export default function NasTaskButton({ task, blocked = false, compact = false }) {
    const nas = useNas();
    const [open, setOpen] = useState(false);
    if (!nas || blocked) return null;
    const archived = nas.findForTask(task);
    const waiting = nas.uploads[task.id]?.status === 'waiting';

    return (
        <>
            {archived && !waiting ? (
                <button type="button" className={`nas-task-btn is-download ${compact ? 'is-compact' : ''}`} onClick={() => nas.openDownload(archived.id)} title={`Tải từ NAS: ${archived.work} (${formatBytes(archived.bytes)})`}>
                    <HardDriveDownload size={13} /> Tải NAS
                </button>
            ) : (
                <button type="button" className={`nas-task-btn ${waiting ? 'is-waiting' : 'is-save'} ${compact ? 'is-compact' : ''}`} onClick={() => setOpen(true)} title={waiting ? 'Đang chờ hoàn tất upload lên NAS' : 'Lưu file audio của task này vào NAS'}>
                    <HardDriveUpload size={13} /> {waiting ? 'Đang lưu NAS' : 'Lưu NAS'}
                </button>
            )}
            {open && <NasUploadModal task={task} onClose={() => setOpen(false)} />}
        </>
    );
}
